import React, { useState } from 'react';
import { ChatHistoryItem } from '../../services/chatHistoryService';
import './ChatHistoryPanel.css';

interface ChatHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  chats: ChatHistoryItem[];
  onLoadChat: (chatId: number) => void;
  onDeleteChat: (chatId: number) => void;
  onRenameChat: (chatId: number, newTitle: string) => void;
  onNewChat: () => void;
  isLoading: boolean;
}

const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  isOpen,
  onClose,
  chats,
  onLoadChat,
  onDeleteChat,
  onRenameChat,
  onNewChat,
  isLoading
}) => {
  const [editingChatId, setEditingChatId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [deletingChatId, setDeletingChatId] = useState<number | null>(null);

  // Close on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    
    // Format as date if older than a week
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const handleStartRename = (chat: ChatHistoryItem) => {
    setEditingChatId(chat.id);
    setEditTitle(chat.chatTitle);
  };

  const handleCancelRename = () => {
    setEditingChatId(null);
    setEditTitle('');
  };

  const handleSaveRename = async (chatId: number) => {
    const trimmed = editTitle.trim();
    if (!trimmed) {
      handleCancelRename();
      return;
    }

    try {
      await onRenameChat(chatId, trimmed);
      setEditingChatId(null);
      setEditTitle('');
    } catch (error) {
      // Error handled by parent
    }
  };

  const handleDelete = async (chatId: number) => {
    if (deletingChatId === chatId) {
      // Second click - confirm delete
      await onDeleteChat(chatId);
      setDeletingChatId(null);
    } else {
      // First click - show confirmation
      setDeletingChatId(chatId);
      setTimeout(() => setDeletingChatId(null), 3000); // Auto-cancel after 3s
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chat-history-panel-overlay" onClick={onClose}>
      <div className="chat-history-panel" onClick={(e) => e.stopPropagation()}>
        <div className="chat-history-header">
          <div className="chat-history-title-section">
            <h2>📚 Saved Conversations</h2>
            <span className="chat-history-count">({chats.length}/10)</span>
          </div>
          <div className="chat-history-header-actions">
            <button
              className="chat-history-new-btn"
              onClick={onNewChat}
              title="Start new chat"
            >
              + New Chat
            </button>
            <button 
              className="chat-history-close" 
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="chat-history-body">
          {isLoading ? (
            <div className="chat-history-loading">
              <div className="loading-spinner"></div>
              <p>Loading saved chats...</p>
            </div>
          ) : chats.length === 0 ? (
            <div className="chat-history-empty">
              <div className="empty-icon">💬</div>
              <h3>No saved conversations yet</h3>
              <p>Start a conversation and click "Save Chat" to save it here.</p>
            </div>
          ) : (
            <div className="chat-history-list">
              {chats.map((chat) => (
                <div key={chat.id} className="chat-history-item">
                  {editingChatId === chat.id ? (
                    /* Edit Mode */
                    <div className="chat-history-edit-mode">
                      <input
                        type="text"
                        className="chat-history-edit-input"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleSaveRename(chat.id);
                          if (e.key === 'Escape') handleCancelRename();
                        }}
                        maxLength={255}
                        autoFocus
                      />
                      <div className="chat-history-edit-actions">
                        <button
                          className="chat-edit-save-btn"
                          onClick={() => handleSaveRename(chat.id)}
                          disabled={!editTitle.trim()}
                        >
                          ✓ Save
                        </button>
                        <button
                          className="chat-edit-cancel-btn"
                          onClick={handleCancelRename}
                        >
                          ✕ Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Display Mode */
                    <>
                      <div 
                        className="chat-history-item-content"
                        onClick={() => onLoadChat(chat.id)}
                      >
                        <h3 className="chat-history-item-title">
                          {chat.chatTitle}
                        </h3>
                        <p className="chat-history-item-summary">
                          {chat.chatSummary}
                        </p>
                        <div className="chat-history-item-meta">
                          <span className="chat-meta-messages">
                            💬 {chat.messageCount} messages
                          </span>
                          <span className="chat-meta-separator">•</span>
                          <span className="chat-meta-time">
                            {formatTimeAgo(chat.lastAccessedAt)}
                          </span>
                          {chat.personaName && (
                            <>
                              <span className="chat-meta-separator">•</span>
                              <span className="chat-meta-persona">
                                🎭 {chat.personaName}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="chat-history-item-actions">
                        <button
                          className="chat-action-btn chat-load-btn"
                          onClick={() => onLoadChat(chat.id)}
                          title="Load this conversation"
                        >
                          📂 Load
                        </button>
                        <button
                          className="chat-action-btn chat-rename-btn"
                          onClick={() => handleStartRename(chat)}
                          title="Rename conversation"
                        >
                          ✏️
                        </button>
                        <button
                          className={`chat-action-btn chat-delete-btn ${deletingChatId === chat.id ? 'confirm-delete' : ''}`}
                          onClick={() => handleDelete(chat.id)}
                          title={deletingChatId === chat.id ? 'Click again to confirm' : 'Delete conversation'}
                        >
                          {deletingChatId === chat.id ? '⚠️ Confirm?' : '🗑️'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryPanel;
















